import { Check } from '@phosphor-icons/react';
import { dailyLimitCents, type AiBudget } from '@shh/shared';
import { Busy, Button, Heading, Text, TextField } from '@shh/ui';
import { useEffect, useState } from 'react';
import { adminAiStats, adminSetAiBudget, friendlyError, type AiDayRow } from '../data/api';
import { adminText } from '../i18n/admin';

/**
 * The AI's cost cap in the admin area (1.28.1, Mike 27.10.: "wir sollten diese Tagesgrenze irgendwie
 * tracken können … wenn wir viele zahlende Nutzer hätten"): the two values, today against the cap
 * and 30 days of history – how often families reached their own cap says when to raise it.
 */
const t = adminText.ai.budget;
const euro = (c: number) =>
  `${(c / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const toCents = (v: string) => Math.round(Number(v.replace(',', '.')) * 100 * 100) / 100;
const toEuro = (c: number) => String(c / 100).replace('.', ',');

export function AiBudgetSection() {
  const [data, setData] = useState<{ budget: AiBudget; days: AiDayRow[] }>();
  const [base, setBase] = useState('');
  const [family, setFamily] = useState('');
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    adminAiStats().then(
      (d) => {
        // Previews run against the live functions: until the server part is live, the older
        // adminAiStats answers without budget/days – say so instead of crashing the admin page.
        if (!d.budget || !Array.isArray(d.days)) return setError(t.notLive);
        setData({ budget: d.budget, days: d.days });
        setBase(toEuro(d.budget.baseCents));
        setFamily(toEuro(d.budget.familyCents));
      },
      (e: unknown) => setError(friendlyError(e)),
    );
  }, []);
  const next = { baseCents: toCents(base), familyCents: toCents(family) };
  const valid = next.baseCents >= 1 && next.familyCents >= 0.1;
  const today = data?.days[0];
  const families = today?.families ?? 0;

  return (
    <section aria-labelledby="ai-budget" className="flex flex-col gap-gap-md">
      <Heading level={2} size="lg" id="ai-budget">
        {t.title}
      </Heading>
      <Text tone="secondary">{t.hint}</Text>
      {error && <p className="font-semibold text-feedback-danger">{error}</p>}
      {!data && !error && <Busy label={adminText.ai.loading} />}
      {data && (
        <>
          <form
            className="flex flex-wrap items-end gap-gap-sm"
            onSubmit={(e) => e.preventDefault()}
          >
            <TextField label={t.base} inputMode="decimal" value={base} onChange={setBase} />
            <TextField label={t.family} inputMode="decimal" value={family} onChange={setFamily} />
            <Button
              icon={Check}
              isDisabled={!valid || busy}
              onPress={() => {
                setBusy(true);
                setSaved(false);
                adminSetAiBudget(next)
                  .then(
                    (b) => {
                      setData({ ...data, budget: b });
                      setSaved(true);
                    },
                    (e: unknown) => setError(friendlyError(e)),
                  )
                  .finally(() => setBusy(false));
              }}
            >
              {t.save}
            </Button>
          </form>
          {saved && (
            <p role="status" className="text-sm font-semibold text-feedback-success">
              {t.saved}
            </p>
          )}
          <Text weight="semibold">
            {t.today(
              euro(today?.cents ?? 0),
              euro(dailyLimitCents(data.budget, families)),
              String(families),
            )}
          </Text>
          {data.days.some((d) => d.familiesAtLimit > 0) && (
            <p className="text-sm font-semibold text-feedback-warning">{t.tight}</p>
          )}
          <Heading level={3} size="sm">
            {t.history}
          </Heading>
          {data.days.length ? (
            <div className="overflow-x-auto rounded-card border border-border-subtle">
              <table className="w-full min-w-xl text-left text-sm tabular-nums">
                <thead className="text-xs text-text-secondary">
                  <tr>
                    <th className="p-inset-xs">{t.day}</th>
                    <th className="p-inset-xs">{t.cost}</th>
                    <th className="p-inset-xs">{t.limit}</th>
                    <th className="p-inset-xs">{t.calls}</th>
                    <th className="p-inset-xs">{t.atLimit}</th>
                    <th className="p-inset-xs">{t.blocked}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.days.map((d) => (
                    <tr key={d.day} className="border-t border-border-subtle">
                      <th scope="row" className="p-inset-xs font-semibold">
                        {d.day}
                      </th>
                      <td className="p-inset-xs">{euro(d.cents)}</td>
                      <td className="p-inset-xs">{d.limitCents ? euro(d.limitCents) : '–'}</td>
                      <td className="p-inset-xs">{d.calls}</td>
                      <td className="p-inset-xs">{d.familiesAtLimit}</td>
                      <td className="p-inset-xs">{d.blockedApp + d.blockedFamily}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Text tone="secondary">{t.none}</Text>
          )}
        </>
      )}
    </section>
  );
}
